import { useState, type ReactNode } from 'react';
import { api, ApiError } from '../../lib/api.js';
import { useQuery } from '../../lib/useQuery.js';
import { useAuth } from '../../lib/auth.js';
import { Page } from '../../app/AppShell.js';
import { Modal } from '../../components/Modal.js';
import { useToast } from '../../components/Toast.js';
import { Loading, ErrorNote, EmptyState } from '../../components/ui.js';
import type { ConsoleUser, Role, SystemRole } from '../../lib/types.js';

// `demo_admin` = admin permissions over only what it created or an admin shared (Share on a list's rows),
// in the Portable CMS app and a restricted console view.
const SYSTEM_ROLES: SystemRole[] = ['owner', 'admin', 'demo_admin', 'editor', 'viewer'];

export function UsersScreen(): ReactNode {
  const users = useQuery(() => api.users(), []);
  const roles = useQuery(() => api.roles(), []);
  const { user: me } = useAuth();
  const toast = useToast();
  const [inviting, setInviting] = useState(false);
  const [invite, setInvite] = useState<string | null>(null);

  const customRoles = (roles.data ?? []).filter((r) => !r.isSystem);
  // The server lists `support` only in an operator organisation.
  const systemRoles: SystemRole[] = roles.data?.some((r) => r.isSystem && r.name === 'support')
    ? [...SYSTEM_ROLES, 'support']
    : SYSTEM_ROLES;

  async function changeRole(u: ConsoleUser, value: string): Promise<void> {
    const body = value.startsWith('custom:')
      ? { roleId: value.slice(7) }
      : { role: value, roleId: null };
    try {
      await api.updateUser(u.id, body);
      toast.push('Role updated', 'success');
      users.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  async function remove(u: ConsoleUser): Promise<void> {
    if (!confirm(`Remove ${u.name}?`)) return;
    try {
      await api.deleteUser(u.id);
      users.refetch();
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Page
      title="Users"
      actions={
        <button className="btn primary" onClick={() => setInviting(true)}>
          Invite user
        </button>
      }
    >
      {users.loading && !users.data ? <Loading what="users" /> : null}
      {users.error ? <ErrorNote error={users.error} /> : null}

      {users.data ? (
        users.data.length === 0 ? (
          <EmptyState>No users yet.</EmptyState>
        ) : (
          <div className="card">
            <table className="grid">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.data.map((u) => (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    <td className="muted small">{u.email}</td>
                    <td>
                      <select
                        value={u.roleId ? `custom:${u.roleId}` : u.role}
                        onChange={(e) => void changeRole(u, e.target.value)}
                      >
                        {systemRoles.map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                        {customRoles.map((r: Role) => (
                          <option key={r.id} value={`custom:${r.id}`}>
                            {r.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      {u.id === me?.id ? (
                        <span className="faint small">you</span>
                      ) : (
                        <button className="btn small danger" onClick={() => void remove(u)}>
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : null}

      {inviting ? (
        <InviteModal
          systemRoles={systemRoles}
          roles={customRoles}
          onClose={() => setInviting(false)}
          onInvited={(url) => {
            setInviting(false);
            users.refetch();
            setInvite(url);
          }}
        />
      ) : null}

      {invite ? (
        <Modal title="Invitation link" onClose={() => setInvite(null)}>
          <p className="muted small">
            Send this link to the new user so they can set a password. It also went to their
            email if mail is configured.
          </p>
          <div className="mono" style={{ wordBreak: 'break-all', padding: '10px 0' }}>{invite}</div>
          <div className="row between">
            <button
              className="btn small"
              onClick={() => {
                void navigator.clipboard?.writeText(invite).then(() => toast.push('Copied'));
              }}
            >
              Copy
            </button>
            <button className="btn primary small" onClick={() => setInvite(null)}>
              Done
            </button>
          </div>
        </Modal>
      ) : null}
    </Page>
  );
}

function InviteModal({
  systemRoles,
  roles,
  onClose,
  onInvited,
}: {
  systemRoles: SystemRole[];
  roles: Role[];
  onClose: () => void;
  onInvited: (inviteUrl: string) => void;
}): ReactNode {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('viewer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();

  async function submit(): Promise<void> {
    setBusy(true);
    setError(undefined);
    try {
      const body = role.startsWith('custom:')
        ? { name: name.trim(), email: email.trim(), role: 'viewer', roleId: role.slice(7) }
        : { name: name.trim(), email: email.trim(), role };
      const created = await api.inviteUser(body);
      onInvited(created.inviteUrl);
    } catch (err) {
      setError(err instanceof ApiError ? (err.fieldMessage() ?? err.message) : String(err));
      setBusy(false);
    }
  }

  return (
    <Modal title="Invite user" onClose={onClose}>
      <div className="stack">
        <label className="field">
          Name
          <input value={name} onChange={(e) => setName(e.target.value)} autoFocus />
        </label>
        <label className="field">
          Email
          <input value={email} type="email" onChange={(e) => setEmail(e.target.value)} />
        </label>
        <label className="field">
          Role
          <select value={role} onChange={(e) => setRole(e.target.value)}>
            {systemRoles.map((r) => (
              <option key={r} value={r}>
                {r}
              </option>
            ))}
            {roles.map((r) => (
              <option key={r.id} value={`custom:${r.id}`}>
                {r.label}
              </option>
            ))}
          </select>
        </label>
        {error ? <div className="form-error">{error}</div> : null}
        <div className="row between">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button
            className="btn primary"
            disabled={busy || name.trim() === '' || email.trim() === ''}
            onClick={() => void submit()}
          >
            {busy ? 'Inviting…' : 'Send invite'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
